import SupportService from "@/services/api/support";
import type { GetSupportResponse, SupportItem, SupportQueryParams } from "@/types/support";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

interface SupportState {
  items: SupportItem[];
  totalItems: number;
  isFetching: boolean;
}

const name = "support";

const initialState: SupportState = {
  items: [],
  totalItems: 0,
  isFetching: false,
};

export const getSupportItems = createAsyncThunk<
  GetSupportResponse<SupportItem>,
  SupportQueryParams | undefined,
  { rejectValue: string }
>(`${name}/getSupportItems`, async (params, { rejectWithValue }) => {
  try {
    return await SupportService.getSupportItems(params);
  } catch (err) {
    const error = err as { message?: string };
    return rejectWithValue(error.message ?? "Failed to fetch support items");
  }
});

const supportSlice = createSlice({
  name,
  initialState,
  reducers: {
    clearSupportState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSupportItems.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(getSupportItems.fulfilled, (state, action: PayloadAction<GetSupportResponse<SupportItem>>) => {
        state.isFetching = false;
        state.items = action.payload.results;
        state.totalItems = action.payload.totalRecord;
      })
      .addCase(getSupportItems.rejected, (state, action) => {
        state.isFetching = false;
        if (import.meta.env.DEV) {
          console.error("Failed to fetch support items:", action.payload);
        }
      });
  },
});

export default supportSlice.reducer;
export const { clearSupportState } = supportSlice.actions;
export type { SupportState };
